import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toUserMessage } from '@platform/api-client';

import { Alert } from '@/shared/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import {
  resolveRedirectTarget,
  type RedirectStateFrom,
} from '@/shared/libs/redirect';
import {
  rememberOnboardingReturn,
  takeOnboardingReturn,
} from '@/shared/libs/onboarding-return';
import { LEGAL_DOCS } from '@/features/legal';
import { AgreementsStep } from '../components/AgreementsStep';
import { useConsentState } from '../api/use-auth.query';
import { useLogout, useSubmitConsents } from '../api/use-auth.mutation';
import { consentItemsFrom, EMPTY_AGREEMENTS } from '../utils/consent';
import type { AgreementValues } from '../types/agreement-step.type';

/**
 * 약관 재동의 View — 동의 사항이 바뀐 개정(개인정보보호법 제15조 제2항) 뒤 로그인한 계정.
 *
 * **차단은 이 화면이 하지 않는다.** Gateway 가 재동의 클레임이 있는 토큰을 이 화면에 필요한 경로 밖에서
 * 403 `AUTH_RECONSENT_REQUIRED` 로 막는다. 여기는 무엇이 바뀌었는지 보여주고 동의를 받는다.
 * 동의하면 토큰을 갱신(클레임 제거)한 뒤 원래 가려던 곳으로 보낸다.
 */
export function ReconsentView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: state, isLoading } = useConsentState();
  const { mutate: submit, isPending, error, reset } = useSubmitConsents();
  const { mutate: logout, isPending: loggingOut } = useLogout();
  const [values, setValues] = useState<AgreementValues>(EMPTY_AGREEMENTS);
  const [fieldError, setFieldError] = useState<string>();

  const stateFrom = (location.state as { from?: RedirectStateFrom } | null)
    ?.from;
  useEffect(() => {
    if (stateFrom)
      rememberOnboardingReturn(
        resolveRedirectTarget({ stateFrom, fallback: PATHS.MYPAGE }),
      );
  }, [stateFrom]);

  // 대상 문서가 없으면(이미 동의했거나 대상 아님) 원래 가려던 곳으로.
  const documents = state?.reconsent.documents ?? [];
  const done = !!state && documents.length === 0;
  const leavingRef = useRef(false);
  useEffect(() => {
    if (!done || leavingRef.current) return;
    leavingRef.current = true;
    navigate(
      resolveRedirectTarget({
        queryFrom: takeOnboardingReturn(),
        fallback: PATHS.MYPAGE,
      }),
      { replace: true },
    );
  }, [done, navigate]);

  if (isLoading || !state || done) return null;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!values.agreeTerms || !values.agreePrivacy) {
      setFieldError('필수 약관에 모두 동의해야 서비스를 이용할 수 있습니다.');
      return;
    }
    setFieldError(undefined);
    submit(consentItemsFrom(values));
  };

  return (
    <div className="mx-auto w-full max-w-md py-10">
      <header className="mb-7">
        <h1 className="text-2xl font-bold -tracking-[0.02em] text-adobe-dark dark:text-neutral-100">
          약관 개정 안내
        </h1>
        <p className="mt-1 break-keep text-sm text-adobe-muted dark:text-neutral-400">
          아래 문서가 개정되었습니다. 계속 이용하시려면 개정된 내용을 확인하고
          동의해 주세요.
        </p>
      </header>

      <ul className="mb-6 space-y-1 text-sm">
        {documents.map((key) => (
          <li key={key} className="flex items-center justify-between">
            <span className="font-semibold text-adobe-dark dark:text-neutral-100">
              {LEGAL_DOCS[key].title}
            </span>
            <span className="text-xs text-adobe-muted dark:text-neutral-400">
              {LEGAL_DOCS[key].version} 시행
            </span>
          </li>
        ))}
      </ul>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <AgreementsStep
          values={values}
          onChange={(next) => {
            setValues(next);
            setFieldError(undefined);
            if (error) reset();
          }}
          errors={fieldError ? { agreeTerms: fieldError } : undefined}
        />

        {error && <Alert tone="error">{toUserMessage(error)}</Alert>}

        <button
          type="submit"
          disabled={isPending}
          className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
        >
          {isPending ? '저장 중…' : '동의하고 계속하기'}
        </button>
      </form>

      {/*
        필수 약관에 동의하지 않으면 이용계약을 이어갈 수 없다 — 나갈 길(로그아웃)을 함께 둔다.
        마이페이지 탈퇴 화면은 다른 API 도 불러 Gateway 에 막히므로 여기서 연결하지 않는다.
      */}
      <p className="mt-8 break-keep text-center text-xs text-adobe-muted dark:text-neutral-500">
        동의하지 않으면 서비스를 이용할 수 없습니다.{' '}
        <button
          type="button"
          onClick={() => logout()}
          disabled={loggingOut}
          className="font-semibold underline underline-offset-2 hover:text-adobe-dark disabled:opacity-50 dark:hover:text-neutral-100"
        >
          로그아웃
        </button>
      </p>
    </div>
  );
}
